import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  listLisMessages,
  type LisMessage,
  type ReportOutcome,
  type ReportOutcomeReason,
} from "@/api/portal";
import { useIAMAuth } from "@/auth/iam";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t, type MessageKey } from "@/i18n";
import { formatWhen } from "@/pages/portal/reportReview";

/**
 * Client Portal: every lab message this organization sent us (docs#182 Gate A).
 *
 * Until docs#182 this was the held-message review queue and nothing else: a lab could see
 * the messages we REFUSED and nothing we accepted. Gate A asks that a lab can see what
 * happened to every message it sends, so this lists every status, filterable, with held
 * as one filter rather than the only view (Baher, docs#182 note 9938).
 *
 * Each row carries two answers, kept apart on purpose:
 *
 * - **Status and outcome code**: what we did with the message (processed, failed, held).
 * - **Smart Report**: whether a report was asked for on it, a closed set
 *   (`requested`, `not_requested`, `generated`, `not_generated`) with a closed-set reason
 *   when there is none. On alpha today every processed message reads "Not requested:
 *   report generation is switched off on this service", which is the true answer and the
 *   one a lab would otherwise have to guess.
 *
 * `not_eligible` is one neutral reason for two refusals (demographics disagreed, or the
 * person has no Medicus account), so the page does not tell a lab which of its patients
 * hold an account. That is a ruling pending with Nadine, not a UI choice.
 *
 * **The empty state here means empty.** A message status carries no PHI at all, so there
 * is no withheld class of row for a blank table to be confused with (unlike the audit
 * viewer, whose rows are PHI-gated).
 */

const FILTERS = {
  all: [],
  held: ["held-for-review"],
  failed: ["failed"],
  processed: ["processed"],
  received: ["received"],
} as const;

type FilterKey = keyof typeof FILTERS;

const WINDOW_HOURS: Record<string, number> = { "24h": 24, "7d": 24 * 7, "30d": 24 * 30 };

const PAGE_SIZE = 50;

const STATUS_COLOR: Record<string, string> = {
  "held-for-review": "orange",
  failed: "red",
  processed: "green",
  received: "gray",
};

const OUTCOME_LABEL: Record<ReportOutcome, MessageKey> = {
  requested: "portal.report_outcome_requested",
  not_requested: "portal.report_outcome_not_requested",
  generated: "portal.report_outcome_generated",
  not_generated: "portal.report_outcome_not_generated",
};

const OUTCOME_COLOR: Record<ReportOutcome, string> = {
  requested: "blue",
  not_requested: "gray",
  generated: "green",
  not_generated: "orange",
};

const REASON_LABEL: Record<ReportOutcomeReason, MessageKey> = {
  generation_off: "portal.report_reason_generation_off",
  not_eligible: "portal.report_reason_not_eligible",
  not_final: "portal.report_reason_not_final",
  not_processed: "portal.report_reason_not_processed",
  request_failed: "portal.report_reason_request_failed",
  generation_failed: "portal.report_reason_generation_failed",
  fhir_read_refused: "portal.report_reason_fhir_read_refused",
};

export function PortalMessagesPage(): JSX.Element {
  const { organizationId } = useIAMAuth();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [timeWindow, setTimeWindow] = useState("7d");
  const [offset, setOffset] = useState(0);

  // Bucket `since` to the minute and put the bucket in the queryKey, so the window
  // advances with the clock instead of freezing at first fetch. Same reasoning as the
  // audit viewer: a stale list on an open screen reads as reassurance.
  const nowMinute = Math.floor(Date.now() / 60_000);
  const since = new Date(
    nowMinute * 60_000 - (WINDOW_HOURS[timeWindow] ?? 168) * 60 * 60 * 1000,
  ).toISOString();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["portal-lis-messages", filter, timeWindow, offset, nowMinute],
    queryFn: () =>
      listLisMessages({
        statuses: [...FILTERS[filter]],
        since,
        limit: PAGE_SIZE,
        offset,
      }),
    retry: false,
    refetchInterval: 60_000,
    // Keep the previous page on screen while the next loads, so paging does not flash
    // the skeleton and, worse, the empty state.
    placeholderData: keepPreviousData,
  });

  function changeFilter(next: FilterKey): void {
    setFilter(next);
    setOffset(0); // a filter change invalidates the page you were on
  }

  function changeWindow(next: string): void {
    setTimeWindow(next);
    setOffset(0);
  }

  const total = data?.total ?? 0;
  const shownTo = Math.min(offset + PAGE_SIZE, total);
  const anyHeld = data?.items.some((m) => m.status === "held-for-review") ?? false;

  return (
    <AppLayout
      breadcrumbs={[{ label: t("portal.title"), to: "/portal" }, { label: t("portal.messages") }]}
    >
      <Group justify="space-between" align="baseline">
        <Title order={1}>{t("portal.messages")}</Title>
        <Text c="dimmed" size="sm">
          {t("portal.organization")}: <Code>{organizationId}</Code>
        </Text>
      </Group>
      <Text c="dimmed" mt="xs">
        {t("portal.messages_subtitle")}
      </Text>

      <Group mt="md" mb="md" align="flex-end">
        <Select
          label={t("portal.held_filter")}
          data={[
            { value: "all", label: t("portal.messages_filter_all") },
            { value: "held", label: t("portal.held_filter_held") },
            { value: "failed", label: t("portal.held_filter_failed") },
            { value: "processed", label: t("portal.messages_filter_processed") },
            { value: "received", label: t("portal.messages_filter_received") },
          ]}
          value={filter}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) changeFilter(v as FilterKey);
          }}
        />
        <Select
          label={t("portal.held_window")}
          data={[
            { value: "24h", label: t("portal.held_window_24h") },
            { value: "7d", label: t("portal.held_window_7d") },
            { value: "30d", label: t("portal.held_window_30d") },
          ]}
          value={timeWindow}
          allowDeselect={false}
          onChange={(v) => {
            if (v !== null) changeWindow(v);
          }}
        />
      </Group>

      {isPending ? (
        <TableSkeleton label={t("portal.messages_loading")} />
      ) : isError ? (
        <Alert color="red" title={t("portal.messages_unavailable")}>
          {error instanceof Error ? error.message : t("authoring.error_generic")}
        </Alert>
      ) : data.items.length === 0 ? (
        <Text c="dimmed">
          {filter === "held" ? t("portal.held_empty") : t("portal.messages_empty")}
        </Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="xs">
            {t("portal.held_showing")} {offset + 1}–{shownTo} {t("portal.held_of")} {total}{" "}
            {t("portal.held_count_suffix")}
          </Text>
          <MessageTable messages={data.items} />
          <Group mt="md">
            <Button
              variant="default"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              {t("portal.held_prev")}
            </Button>
            <Button
              variant="default"
              disabled={shownTo >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              {t("portal.held_next")}
            </Button>
          </Group>
          {filter === "held" || anyHeld ? (
            <Alert color="blue" mt="lg" title={t("portal.held_what_now")}>
              <Text size="sm">{t("portal.held_what_now_note")}</Text>
            </Alert>
          ) : null}
        </>
      )}
    </AppLayout>
  );
}

function ReportCell({ message }: { message: LisMessage }): JSX.Element {
  if (message.report_outcome === null) {
    return <Text c="dimmed">{t("portal.report_outcome_none")}</Text>;
  }
  return (
    <Badge color={OUTCOME_COLOR[message.report_outcome]} variant="light">
      {t(OUTCOME_LABEL[message.report_outcome])}
    </Badge>
  );
}

function ReasonCell({ message }: { message: LisMessage }): JSX.Element {
  const reason = message.report_outcome_reason;
  if (reason === null) return <>—</>;
  // The human sentence AND the closed-set code, so a lab's integrator can match what the
  // API returns without reverse-engineering the English.
  return (
    <Stack gap={2}>
      <Text size="sm">{t(REASON_LABEL[reason])}</Text>
      <Code>{reason}</Code>
    </Stack>
  );
}

function MessageTable({ messages }: { messages: LisMessage[] }): JSX.Element {
  return (
    <Table striped highlightOnHover withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("portal.held_col_message")}</Table.Th>
          <Table.Th>{t("portal.held_col_status")}</Table.Th>
          <Table.Th>{t("portal.messages_col_outcome")}</Table.Th>
          <Table.Th>{t("portal.messages_col_report")}</Table.Th>
          <Table.Th>{t("portal.messages_col_report_reason")}</Table.Th>
          <Table.Th>{t("portal.messages_col_report_id")}</Table.Th>
          <Table.Th>{t("portal.messages_col_updated")}</Table.Th>
          <Table.Th>{t("portal.held_col_first_seen")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {messages.map((message) => (
          <Table.Tr key={message.message_id}>
            <Table.Td>
              <Code>{message.message_id}</Code>
            </Table.Td>
            <Table.Td>
              <Badge color={STATUS_COLOR[message.status] ?? "blue"} variant="light">
                {message.status}
              </Badge>
            </Table.Td>
            <Table.Td>{message.code ?? "—"}</Table.Td>
            <Table.Td>
              <ReportCell message={message} />
            </Table.Td>
            <Table.Td>
              <ReasonCell message={message} />
            </Table.Td>
            <Table.Td>{message.report_id ? <Code>{message.report_id}</Code> : "—"}</Table.Td>
            {/* docs#239 note 19795 item 43: read like the report pages, the zone named,
                not raw UTC ISO. The browser's zone: this page is not told the lab's. */}
            <Table.Td>{formatWhen(message.updated_at)}</Table.Td>
            <Table.Td>{formatWhen(message.created_at)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
